import React, { memo, useCallback, useContext, useRef, useMemo, useEffect, useId, useState } from 'react';
import type { CSSProperties } from 'react';
import type { OptionsList as OptionsListType, OptionsItem as OptionsItemType, StepNode } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { resolveLabel } from '../../../utils/contentResolver';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { seededFisherYatesShuffle } from '../../../utils/shuffle';
import { OptionsRandomizationContext } from '../../../context/OptionsRandomizationContext';
import { resolveScreenForegroundColor } from '../../../utils/canvasForeground';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { OptionsItem } from '../OptionsItem';
import styles from './OptionsList.module.css';

interface IndexedItem {
  item: OptionsItemType;
  originalIndex: number;
}

/**
 * How many options render before the rest go behind "More options" (W8 M5/S3,
 * ruling R-W8A-10).
 *
 * The SAME number as the compiler's `SINGLE_PICK_OPTION_CAP`
 * (`strategy-blocks/utils/compiledScreenContract`, which this module cannot
 * import — screen-view-v2 has no strategy-blocks dependency). One number, two
 * layers: a list the compile check `options.optionOverflow` does not warn about
 * never grows a disclosure, and every list it does warn about gets one. Two
 * different numbers would produce a screen that is either warned-about-but-whole
 * or silently truncated.
 *
 * Why a disclosure at all: `screen21` of the W6-fix flagship offered 5 of its 10
 * declared options because generation dropped the tail, and D22 forbids exactly
 * that fix. The list stays whole in the document; the long tail is the runtime's
 * problem. Design brief: `.design/webshell-a11y/w8-more-options/`.
 */
export const OPTIONS_DISCLOSURE_THRESHOLD = 8;

/**
 * An option that must stay VISIBLE even while the list is collapsed — an
 * opt-out, an "other", a "none of the above".
 * survey-methodology-and-measurement.md § 6.2 requires a sensitive item to
 * carry an explicit opt-out, and an opt-out hidden behind a disclosure is one
 * the visitor who needs it does not find. It is also the same set W8(f) refuses
 * to randomize, for the same reason: its position is load-bearing.
 *
 * **Mirror** of `ANCHORED_OPTION_VALUE_PATTERN` in
 * `strategy-blocks/utils/compiledScreenContract.ts`. The `(?![a-z])` followers
 * are a trailing boundary a plain `\b` cannot give: `_` is a word character, so
 * `\b` would refuse `prefer_not_to_say` while still accepting "I prefer
 * nothing sweet". `screen-view-v2` has no
 * strategy-blocks dependency (the arrow points the other way), so the two are
 * kept identical by a parity test that lives on the side that CAN import both:
 * `strategy-blocks/utils/__tests__/optionAnchorParity.test.ts`.
 */
export const ANCHORED_OPTION_PATTERN =
  /prefer[\s_-]?not(?![a-z])|no[\s_-]?answer(?![a-z])|rather[\s_-]?not(?![a-z])|not[\s_-]?say(?![a-z])|not[\s_-]?sure(?![a-z])|none[\s_-]?of(?![a-z])|^none$|^other$|_other$|^skip$/i;

function isAnchoredOption(item: OptionsItemType, locale: string): boolean {
  const value = typeof item.variableValue === 'string' ? item.variableValue : '';
  const title = resolveLabel(item.title, locale) ?? '';
  return ANCHORED_OPTION_PATTERN.test(value) || ANCHORED_OPTION_PATTERN.test(title);
}

function isMaxCellHeightEnabled(value: unknown): boolean {
  return value === true || value === 'true';
}

export interface OptionsListProps {
  options: OptionsListType | undefined;
  locale: string;
  variables?: TemplateVariables;
  selectedIndices?: Set<number>;
  onSelect?: (item: OptionsItemType, index: number, selected: boolean) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
  /**
   * Accessible name for the option group. A `radiogroup` with no name is announced as an
   * unlabelled group, so the screen passes its own question title down (WCAG 4.1.2 / the
   * WAI-ARIA radiogroup pattern).
   */
  ariaLabel?: string;
  /**
   * The screen this list renders on. Read ONLY for the canvas ground the "More options"
   * disclosure has to be legible against (WP8 UX U-1 / D-W10-2) — see
   * `disclosureLabelColor` below. Optional: the editor and the SSR pre-pass render lists
   * without a step, and a list that cannot name a ground changes nothing.
   */
  step?: StepNode;
}

export const OptionsList = memo(function OptionsList({
  options,
  locale,
  variables,
  selectedIndices,
  onSelect,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
  ariaLabel,
  step,
}: OptionsListProps): React.ReactElement | null {
  const listAnim = useElementAnimation(editPath);

  // ⚠️ Rules of Hooks: the empty-options early-return lives BELOW every hook.
  // Options can materialize on a mounted component mid-session (theme cascade,
  // editor writes) — an early return above the hooks flips the hook count and
  // crashes with "Rendered more hooks than during the previous render".
  const {
    selectionMode,
    columnLayout,
    itemLayout,
    appearance,
    selectedAppearance,
    layout,
    isRandomized,
    maxSelectedItemCount,
  } = options ?? {};
  const items = options?.items ?? [];

  // cellHeightFromCellWidth is a v2.11 field not yet in generated types
  const cellHeightFromCellWidth = (options as (OptionsListType & { cellHeightFromCellWidth?: number }) | null | undefined)?.cellHeightFromCellWidth;

  const selectable = selectionMode !== 'browse';
  const isTwoCols = columnLayout === 'twoCols';
  const useEqualHeights = isMaxCellHeightEnabled(appearance?.useMaxCellHeight);

  // Deterministic shuffle: the seed arrives via context — the runtime engine
  // provides the visitor's (`${platformUserId}:${screenId}`), the editor's
  // canvas and preview provide the deterministic `preview:${screenId}`
  // (R-W11-5). No provider (SSR, entry screen, surfaces that render a template
  // rather than a funnel screen) → manifest order.
  const randomization = useContext(OptionsRandomizationContext);

  const displayItems = useMemo(() => {
    const indexed: IndexedItem[] = items.map((item, i) => ({ item, originalIndex: i }));
    if (!isRandomized || !randomization) {
      return indexed;
    }
    return seededFisherYatesShuffle(indexed, randomization.seedKey);
  }, [items, isRandomized, randomization]);

  // W8 M5/S3 — the "More options" disclosure (rulings R-W8A-10 … R-W8A-14).
  //
  //  · selectable lists only: a `browse` list is a HeroList's benefit rows or a
  //    paywall's feature list — value copy, not answers, and hiding it would
  //    hide what the visitor is being sold (R-W8A-13);
  //  · in EVERY mode — published runtime, canvas node, edit-mode preview
  //    (owner ruling R-POST-3, controller ruling R-W11-4, superseding
  //    R-W8A-14's editor exemption). The editor exists to show the owner the
  //    screen they are about to publish, and a list that collapses for the
  //    visitor but not for the owner shows them a screen that does not exist.
  //    No option becomes unreachable: the disclosure button expands the list in
  //    edit mode exactly as it does live, and the options EDITOR is the
  //    right-hand `V2OptionsListSection`, which lists `content.options.items`
  //    in document order regardless of what the preview shows.
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const disclosureRef = useRef<HTMLButtonElement>(null);
  const pendingFocusIndex = useRef<number | null>(null);
  const collapseFocusPending = useRef(false);

  // The disclosure's words are the OWNER's, carried by the compiled document
  // (`content.options.moreOptionsLabel` / `fewerOptionsLabel`, LocalizedLabel —
  // schema-models 3.1.0-strategy.4), the same way `paywall.restoreLabel` is.
  // Controller ruling R-W70-37, the same class as R-W70-35: the platform does
  // not author what the visitor reads. A document carrying no labels therefore
  // gets NO disclosure and renders the whole list — the runtime would otherwise
  // have to invent an English string, which is exactly the thing forbidden.
  // `options?.`: the empty-options guard sits BELOW every hook (Rules of Hooks), so these
  // run for an undefined list too.
  const moreOptionsText = resolveLabel(options?.moreOptionsLabel ?? undefined, locale);
  const fewerOptionsText = resolveLabel(options?.fewerOptionsLabel ?? undefined, locale);
  const hasDisclosureCopy = Boolean(moreOptionsText && fewerOptionsText);

  // WP8 UX U-1 / D-W10-2 — the disclosure's own foreground.
  //
  // It used to be `color: inherit` in the CSS module, i.e. the engine's default black, and
  // on the flagship's `#030103` canvas that measured 1.01:1: a control present, focusable,
  // clickable and invisible. This is the SAME defect and the SAME fix as `Button` and
  // `PaywallHeader` (WP8 UX M-4): the higher-contrast of black/white against the ground the
  // screen ITSELF declares, which reproduces today's black on every light canvas and only
  // changes what was unreadable. No brand colour is introduced.
  //
  // The OWNER's colour wins wherever they named one: the disclosure's words are theirs
  // (`moreOptionsLabel` / `fewerOptionsLabel`, ruling R-W70-37) and so is any colour on
  // them. A screen that declares no resolvable ground keeps `inherit`.
  const activeDisclosureLabel = optionsExpanded ? options?.fewerOptionsLabel : options?.moreOptionsLabel;
  const authoredDisclosureColor = (activeDisclosureLabel as { appearance?: { color?: string } } | null | undefined)
    ?.appearance?.color;
  const disclosureLabelColor = authoredDisclosureColor
    ?? resolveScreenForegroundColor(step as Parameters<typeof resolveScreenForegroundColor>[0]);

  const collapsible = selectable
    && hasDisclosureCopy
    && displayItems.length > OPTIONS_DISCLOSURE_THRESHOLD;

  // The first N of the DISPLAY order, so a randomized long list shows a random
  // eight rather than always the same eight — the disclosure does not defeat
  // W8(f)'s shuffle. **An ANCHORED option is never in the hidden tail** (fix
  // round 1): § 6.2 requires a sensitive item's opt-out to be reachable, and an
  // opt-out behind a disclosure is one the visitor who needs it does not find.
  // It is counted INTO the eight rather than shown as a ninth, so the cap the
  // compile check enforces stays the cap the visitor sees.
  const visibleItems = useMemo(() => {
    if (!collapsible || optionsExpanded) return displayItems;
    const anchored = displayItems.filter(({ item }) => isAnchoredOption(item, locale));
    const budget = Math.max(OPTIONS_DISCLOSURE_THRESHOLD - anchored.length, 0);
    const head = new Set(
      displayItems.filter((entry) => !anchored.includes(entry)).slice(0, budget),
    );
    return displayItems.filter((entry) => head.has(entry) || anchored.includes(entry));
  }, [collapsible, optionsExpanded, displayItems, locale]);
  const hiddenCount = displayItems.length - visibleItems.length;

  /**
   * Where focus goes when the list opens: the first item that was NOT visible,
   * by its position in the expanded DOM.
   *
   * Computed, not assumed. It used to be `OPTIONS_DISCLOSURE_THRESHOLD`, which
   * is only correct when the visible set is a plain prefix — and it stopped
   * being one the moment an anchored opt-out was pulled forward into it. With
   * an opt-out present the visible set is "the first seven, plus the last one",
   * so the eighth item is hidden and hard-coding 8 skipped it.
   */
  const firstHiddenIndex = useMemo(() => {
    const visible = new Set(visibleItems);
    return displayItems.findIndex((entry) => !visible.has(entry));
  }, [displayItems, visibleItems]);

  const toggleOptionsExpanded = useCallback(() => {
    setOptionsExpanded((expanded) => {
      // Opening moves focus to the first NEWLY revealed option, so a keyboard
      // user continues where the list continues instead of being dropped at the
      // top. Closing leaves focus on the button, which is still there.
      pendingFocusIndex.current = expanded || firstHiddenIndex < 0 ? null : firstHiddenIndex;
      collapseFocusPending.current = expanded;
      return !expanded;
    });
  }, [firstHiddenIndex]);

  useEffect(() => {
    const index = pendingFocusIndex.current;
    pendingFocusIndex.current = null;
    if (index === null) {
      // Collapsing UNMOUNTS the tail. If focus was on one of those options it
      // would be lost to `document.body` — the classic disclosure regression —
      // so it comes back to the button, which is still there.
      if (collapseFocusPending.current) {
        collapseFocusPending.current = false;
        disclosureRef.current?.focus();
      }
      return;
    }
    if (!gridRef.current) return;
    const revealed = gridRef.current.querySelectorAll<HTMLElement>(
      ':scope > [role="radio"], :scope > [role="checkbox"]',
    )[index];
    revealed?.focus();
  }, [optionsExpanded]);

  // A list that shrinks below the threshold (an edit, a different screen) must
  // not stay stuck open with a button that no longer exists.
  useEffect(() => {
    if (!collapsible && optionsExpanded) setOptionsExpanded(false);
  }, [collapsible, optionsExpanded]);

  // Max selection reached: disable unselected items
  const maxReached = maxSelectedItemCount != null
    && (selectedIndices?.size ?? 0) >= maxSelectedItemCount;

  // Measure max cell height for equalHeights. Grid uses grid-auto-rows; list uses child min-height.
  const gridRef = useRef<HTMLDivElement>(null);
  const [measuredRowHeight, setMeasuredRowHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!useEqualHeights || !gridRef.current) {
      setMeasuredRowHeight(null);
      return;
    }
    // Measure after render: find the tallest child
    const children = gridRef.current.children;
    let maxH = 0;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      const h = Math.max(child.scrollHeight, child.getBoundingClientRect().height);
      if (h > maxH) maxH = h;
    }
    const roundedMaxH = Math.ceil(maxH);
    if (roundedMaxH > 0 && roundedMaxH !== measuredRowHeight) {
      setMeasuredRowHeight(roundedMaxH);
    }
  }, [visibleItems, isTwoCols, measuredRowHeight, useEqualHeights]);

  const equalHeightStyle = measuredRowHeight != null
    ? ({ '--sv2-options-equal-cell-height': `${measuredRowHeight}px` } as CSSProperties)
    : {};

  const containerStyle: CSSProperties = {
    ...mapFrameLayoutStyles(layout),
    ...(appearance?.itemsSpacing !== undefined && { gap: appearance.itemsSpacing }),
    ...(isTwoCols && { alignItems: useEqualHeights ? 'stretch' : 'start' }),
    ...(measuredRowHeight != null && isTwoCols && { gridAutoRows: measuredRowHeight }),
    ...equalHeightStyle,
    ...listAnim.style,
  };

  const containerClassName = mergeAnimationClassName(
    [
      isTwoCols ? styles.grid : styles.list,
      useEqualHeights && measuredRowHeight != null ? styles.equalHeights : undefined,
      className,
    ].filter(Boolean).join(' '),
    listAnim,
  );

  const handleSelect = useCallback(
    (item: OptionsItemType, index: number, selected: boolean) => {
      if (onSelect) {
        onSelect(item, index, selected);
      }
    },
    [onSelect]
  );

  // D-T9b-14 — arrow-key navigation inside the option group.
  // Activation (Space/Enter) belongs to the option itself (utils/optionItemA11y.ts);
  // MOVEMENT belongs here, because only the container knows the sibling order. Focus is
  // moved through the rendered DOM order, so a randomized list navigates as it reads.
  const handleContainerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!selectable) return;
      const NAV_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (!NAV_KEYS.includes(event.key)) return;

      const container = event.currentTarget;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>(':scope > [role="radio"], :scope > [role="checkbox"]')
      );
      if (items.length === 0) return;

      const current = items.indexOf(document.activeElement as HTMLElement);
      if (current === -1) return;

      let next: number;
      switch (event.key) {
        case 'Home': next = 0; break;
        case 'End': next = items.length - 1; break;
        case 'ArrowUp':
        case 'ArrowLeft': next = (current - 1 + items.length) % items.length; break;
        default: next = (current + 1) % items.length; break;
      }

      event.preventDefault();
      items[next]?.focus();
    },
    [selectable]
  );

  const schemaTestId = testId ?? getSchemaTestId(editPath);
  // `aria-controls` needs a real id on the container the button expands.
  const generatedId = useId();
  const containerId = schemaTestId ? `${schemaTestId}-group` : generatedId;

  // A single-pick list is a radiogroup; a multi-pick list is a plain group (ARIA has no
  // "checkboxgroup"). A browse list is decorative and keeps no role at all.
  const containerRole = !selectable
    ? undefined
    : selectionMode === 'many' ? 'group' : 'radiogroup';

  if (!options || !options.items?.length) {
    return null;
  }

  return (
    <>
    <div
      ref={gridRef}
      id={containerId}
      className={containerClassName}
      style={containerStyle}
      role={containerRole}
      aria-label={containerRole && ariaLabel ? ariaLabel : undefined}
      onKeyDown={selectable ? handleContainerKeyDown : undefined}
      data-anim={listAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {visibleItems.map(({ item, originalIndex }, displayIndex) => {
        const isSelected = selectedIndices?.has(originalIndex) ?? false;

        // Override checkbox checked state when selected
        const effectiveItem = isSelected && item.checkbox
          ? { ...item, checkbox: { ...item.checkbox, checked: true } }
          : item;

        return (
          <OptionsItem
            key={item.variableValue ?? `item-${originalIndex}`}
            item={effectiveItem}
            itemLayout={itemLayout}
            index={originalIndex}
            locale={locale}
            variables={variables}
            selectable={selectable}
            selected={isSelected}
            disabled={maxReached && !isSelected}
            appearance={appearance}
            selectedAppearance={selectedAppearance}
            onSelect={handleSelect}
            editPath={editPath ? `${editPath}.items.${originalIndex}` : undefined}
            staggerIndex={displayIndex}
            previewMode={previewMode}
            cellHeightFromCellWidth={cellHeightFromCellWidth}
            columnLayout={columnLayout}
            selectionMode={selectionMode}
          />
        );
      })}
    </div>
    {collapsible && (
      /* A SIBLING of the group, never a child: the container is a `radiogroup`
         (or `group`) and a button inside it is not a radio. Sibling placement
         also keeps it out of `.grid`'s `1fr 1fr` columns and out of
         `.equalHeights > *`'s min-height, either of which would make it look
         like one more answer (R-W8A-12). Every option is `tabIndex = 0`
         (D-T9b-14 — this component deliberately has no roving tabindex), so
         the button is simply the next tab stop after the last visible option. */
      <button
        ref={disclosureRef}
        type="button"
        className={styles.moreOptions}
        style={disclosureLabelColor ? { color: disclosureLabelColor } : undefined}
        aria-expanded={optionsExpanded}
        aria-controls={containerId}
        onClick={toggleOptionsExpanded}
        data-testid={schemaTestId ? `${schemaTestId}.more` : undefined}
      >
        {/* One text node, not label + count as siblings: the accessible name is
            computed by concatenating them, and two nodes produced
            "More options(3)" with no separator. Visible label and accessible
            name must be the same string (WCAG 2.5.3). The words are the
            owner's; only the count is the platform's, and a numeral is not
            copy. */}
        <span>{optionsExpanded ? fewerOptionsText : `${moreOptionsText} (${hiddenCount})`}</span>
        <span className={styles.moreOptionsCaret} aria-hidden="true" />
      </button>
    )}
    </>
  );
});

OptionsList.displayName = 'OptionsList';
